
const invalidationWaves = 1;

/**
 * Управляет портфелем сделок, поддерживая частичные входы, выходы и трейлинг-стопы.
 * Записывает полную историю исполнения для детального анализа и отрисовки.
 */
export class PortfolioManager {
  /**
   * @param {number} initialBalance - Начальный баланс.
   * @param {object} fees - Объект с комиссиями { market, limit }.
   * @param {number} cashbackRate - Коэффициент кэшбэка.
   */
  constructor(initialBalance, fees, cashbackRate) {
    this.balance = initialBalance;
    this.initialBalance = initialBalance;
    this.equity = initialBalance;
    this.pendingPositions = [];
    this.activePositions = [];
    this.closedTrades = [];
    this.cancelledTrades = [];
    this.fees = fees;
    this.cashbackRate = cashbackRate;
  }

  /**
   * Добавляет новую, еще не активную позицию в портфель.
   * @param {Position} positionObject - Экземпляр класса Position.
   */
  addPosition(positionObject) {
    this.pendingPositions.push(positionObject);
  }

  /**
   * Главный метод обновления, вызываемый на каждой свече.
   * @param {object} candle - Текущая свеча.
   * @param {number} currentWaveCount - Текущее общее количество волн.
   */
  update(candle, currentWaveCount) {
    // Проверяем отложенные позиции на ПЕРВЫЙ вход и активацию
    this._checkPendingForActivation(candle);

    // Управляем уже активными позициями
    this._manageActivePositions(candle);

    // Отменяем устаревшие отложенные ордера
    this._invalidateStalePositions(currentWaveCount);

    // Обновляем эквити
    this._updateEquity(candle.close);
  }

  /**
   * Удаляет отложенные позиции, которые стали неактуальными.
   */
  _invalidateStalePositions(currentWaveCount) {
    const positionsToCancel = [];
    const invalidationThreshold = invalidationWaves || 3;

    for (const position of this.pendingPositions) {
      if (currentWaveCount >= position.creationWaveCount + invalidationThreshold) {
        positionsToCancel.push(position);
      }
    }

    if (positionsToCancel.length > 0) {
      this.pendingPositions = this.pendingPositions.filter(p => !positionsToCancel.includes(p));
      positionsToCancel.forEach(p => {
        p.status = 'closed';
        p.closeReason = 'Cancelled';
        this.cancelledTrades.push(p);
      });
    }
  }

  /**
   * Проверяет "спящие" позиции на активацию.
   */
  _checkPendingForActivation(candle) {
    const activatedPositions = [];
    for (const position of this.pendingPositions) {
      // _processEntries вернет true, если был исполнен хотя бы один ордер
      if (this._processEntries(position, candle)) {
        activatedPositions.push(position);
      }
    }

    if (activatedPositions.length > 0) {
      this.activePositions.push(...activatedPositions);
      this.pendingPositions = this.pendingPositions.filter(p => !activatedPositions.includes(p));
    }
  }

  /**
   * Управляет активными позициями.
   */
  _manageActivePositions(candle) {
    const closedPositionsIndexes = [];

    for (let i = 0; i < this.activePositions.length; i++) {
      const position = this.activePositions[i];
      const isLong = position.direction === 'long';

      // --- 2. Проверка Stop Loss (включая трейлинг) ---
      let stopLabel
      let effectiveStopLoss

      if (isLong) {
        if (position.stopLoss >= position.trailingStopLoss) {
          stopLabel = 'SL';
          effectiveStopLoss = position.stopLoss;
        } else {
          stopLabel = 'TSL';
          effectiveStopLoss = position.trailingStopLoss;
        }
      } else {
        if (position.stopLoss <= position.trailingStopLoss) {
          stopLabel = 'SL';
          effectiveStopLoss = position.stopLoss;
        } else {
          stopLabel = 'TSL';
          effectiveStopLoss = position.trailingStopLoss;
        }
      }

      if ((isLong && candle.low <= effectiveStopLoss) || (!isLong && candle.high >= effectiveStopLoss)) {
        this._closePosition(position, candle, effectiveStopLoss, stopLabel);
        closedPositionsIndexes.push(i);
        continue; // Позиция закрыта, переходим к следующей
      }

      // Сначала всегда проверяем, не нужно ли добрать позицию
      this._processEntries(position, candle);


      // --- 1. Обновление Trailing Stop ---
      if (position.tslDistance > 0) {
        const newTsl = isLong ? candle.high - position.tslDistance : candle.low + position.tslDistance;
        if ((isLong && newTsl > position.trailingStopLoss) || (!isLong && newTsl < position.trailingStopLoss)) {
          position.trailingStopLoss = newTsl;
        }
      }


      // --- 3. Проверка Take Profit (с частичным закрытием) ---
      if (position.takeProfits.length === 0) continue;

      const nextTp = position.takeProfits[0];

      if ((isLong && candle.high >= nextTp) || (!isLong && candle.low <= nextTp)) {
        if (position.takeProfits.length > 1) { // Это TP1
          const sizeToClose = position.currentSize * position.partialClosePercent;
          this._closePartialPosition(position, candle, nextTp, 'TP1', sizeToClose);
          position.stopLoss = position.averageEntryPrice; // Двигаем стоп в безубыток
          position.takeProfits.shift(); // Удаляем сработавший TP
        } else { // Это финальный TP2
          this._closePosition(position, candle, nextTp, 'TP2');
          closedPositionsIndexes.push(i);
        }
      }
    }

    // Очищаем массив активных позиций от закрытых (безопасным способом)
    if (closedPositionsIndexes.length > 0) {
      this.activePositions = this.activePositions.filter((_, index) => !closedPositionsIndexes.includes(index));
    }
  }

  /**
   * Частично закрывает позицию.
   */
  _closePartialPosition(position, candle, closePrice, reason, sizeToClose) {
    if (sizeToClose <= 0) return;
    if (sizeToClose > position.currentSize) {
      sizeToClose = position.currentSize;
    }

    const pnlMultiplier = position.direction === 'long' ? 1 : -1;
    const pnlGross = (closePrice - position.averageEntryPrice) * sizeToClose * pnlMultiplier;
    const fee = (sizeToClose * this.fees.limit) + (sizeToClose * this.fees.market); // Упрощенно, комиссия на вход + выход
    const effectiveFee = fee * (1 - this.cashbackRate);
    const pnlNet = pnlGross - effectiveFee;

    this.balance += pnlNet;
    position.pnl += pnlNet;
    position.currentSize -= sizeToClose;

    // --- ИЗМЕНЕНИЕ --- Записываем частичный выход в историю
    position.exitOrders.push({
      time: candle.time,
      price: closePrice,
      size: sizeToClose,
      reason: reason
    });
  }

  /**
   * Обрабатывает ордера на вход для ОДНОЙ позиции.
   */
  _processEntries(position, candle) {
    let hasFilledOrdersThisCall = false;
    for (const order of position.entryOrders) {
      if (order.status === 'pending') {
        const isLongTrigger = position.direction === 'long' && candle.low <= order.price;
        const isShortTrigger = position.direction === 'short' && candle.high >= order.price;

        if (isLongTrigger || isShortTrigger) {
          order.status = 'filled';
          order.executionTime = candle.time; // --- ИЗМЕНЕНИЕ --- Записываем время исполнения

          if (position.status === 'pending') {
            position.status = 'active';
            position.openTime = candle.time;
          }

          hasFilledOrdersThisCall = true;

          const totalValue = (position.averageEntryPrice * position.currentSize) + (order.price * order.size);
          position.currentSize += order.size;
          if (position.currentSize > 0) {
            position.averageEntryPrice = totalValue / position.currentSize;
          }
        }
      }
    }
    return hasFilledOrdersThisCall;
  }

  /**
   * (Усиленная версия) Закрывает активную позицию.
   */
  _closePosition(position, candle, closePrice, reason) {
    if (typeof position.averageEntryPrice !== 'number' || isNaN(position.averageEntryPrice) ||
      typeof position.currentSize !== 'number' || position.currentSize <= 0) {
      console.error(`КРИТИЧЕСКАЯ ОШИБКА в _closePosition: #${position.originId}`, { avgEntry: position.averageEntryPrice, size: position.currentSize });
      position.status = 'closed';
      position.closeReason = 'ERROR';
      this.closedTrades.push(position);
      return;
    }

    // --- ИЗМЕНЕНИЕ --- Записываем финальный выход в историю
    position.exitOrders.push({
      time: candle.time,
      price: closePrice,
      size: position.currentSize,
      reason: reason
    });

    const pnlMultiplier = position.direction === 'long' ? 1 : -1;
    const pnlGross = (closePrice - position.averageEntryPrice) * position.currentSize * pnlMultiplier;
    const fee = (position.currentSize * this.fees.limit) + (position.currentSize * this.fees.market);
    const effectiveFee = fee * (1 - this.cashbackRate);
    const pnlNet = pnlGross - effectiveFee;

    position.pnl += pnlNet; // Добавляем к уже накопленному PnL от частичных выходов
    position.fee += effectiveFee; // То же для комиссий
    this.balance += pnlNet;

    position.status = 'closed';
    position.closeTime = candle.time;
    position.closeReason = reason;
    position.actualClosePrice = closePrice;
    position.currentSize = 0; // Обнуляем размер

    this.closedTrades.push(position);
  }

  /**
   * Обновляет эквити (баланс + нереализованный PnL).
   */
  _updateEquity(currentPrice) {
    let unrealizedPnl = 0;
    for (const position of this.activePositions) {
      if (position.currentSize > 0) {
        const pnlMultiplier = position.direction === 'long' ? 1 : -1;
        unrealizedPnl += (currentPrice - position.averageEntryPrice) * position.currentSize * pnlMultiplier;
      }
    }
    this.equity = this.balance + unrealizedPnl;
  }

  /**
   * Возвращает итоговую статистику по бэктесту.
   */
  getStats() {
    const totalTrades = this.closedTrades.length;
    const winningTrades = this.closedTrades.filter(t => t.pnl > 0).length;
    const losingTrades = totalTrades - winningTrades;
    const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;

    return {
      initialBalance: this.initialBalance,
      finalBalance: this.balance,
      totalPnl: this.balance - this.initialBalance,
      totalTrades,
      winningTrades,
      losingTrades,
      winRate: winRate.toFixed(2),
      cancelledTradesCount: this.cancelledTrades.length,
      closedTrades: this.closedTrades,
    };
  }
}

/**
 * Представляет одну торговую позицию со всей необходимой информацией
 * для исполнения, анализа и максимально детальной отрисовки.
 */
export class Position {
  constructor(params) {
    const {
      direction, entryOrders, stopLoss, takeProfits, tslDistance,
      partialClosePercent, originId, creationWaveCount, creationTime, metadata = {}
    } = params;

    // --- 1. Идентификация и Статус ---
    this.id = params.id || `${params.originId}-${params.creationWaveCount}`;
    this.status = 'pending';
    this.originId = originId;
    this.creationWaveCount = creationWaveCount;
    this.creationTime = creationTime;

    // --- 2. Параметры Сделки (исходные) ---
    this.direction = direction;
    this.initialStopLoss = stopLoss;       // Первоначальный стоп
    this.initialTakeProfits = [...takeProfits]; // Копия исходных тейков
    this.tslDistance = tslDistance || 0;
    this.partialClosePercent = partialClosePercent || 0.5;

    // --- 3. Ордера на Вход (с местом для времени исполнения) ---
    this.entryOrders = entryOrders.map(order => ({
      price: order.price,
      size: order.size,
      status: 'pending',
      executionTime: null // <-- Здесь будет время исполнения каждого входа
    }));

    // --- 4. Ордера на Выход (для точной отрисовки) ---
    this.exitOrders = []; // <-- Здесь будет история всех выходов

    // --- 5. Динамические параметры Исполнения ---
    this.openTime = null;
    this.closeTime = null;
    this.currentSize = 0;
    this.averageEntryPrice = 0;
    this.stopLoss = stopLoss; // Текущий, может двигаться в безубыток
    this.takeProfits = [...takeProfits].sort((a, b) => direction === 'long' ? a - b : b - a);
    this.trailingStopLoss = direction === 'long' ? -Infinity : Infinity;

    // --- 6. Результаты ---
    this.closeReason = null;
    this.actualClosePrice = null;
    this.pnl = 0;
    this.fee = 0;

    // --- 7. Метаданные для отрисовки ---
    this.metadata = metadata;
  }
}
